import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import * as R from 'ramda';
import { convertFromRaw, EditorState } from 'draft-js';

import FormSettings from './FormSettings';
import { updateOrgs, isEditAction, isCopyAction, isCancelAction, getCopiedFormName } from './utils';
import { closeDialog } from '../../../store/dialogs/actions';
import {updateFormSettingsFromManager, copyForm, resetSelectedForm, getForms} from '../../../store/forms/actions';
import { updateFormSettingsFromEditor } from '../../../store/formEditor/actions';
import { decorator } from '../../UI/RichContent';
import {isFormOwner} from "../../../store/forms/utils";

function EditForm (props) {
    const [editor, setEditor] = useState(EditorState.createEmpty(decorator));
    const [formData, setFormData] = useState(null);
    const [editData, setEditData] = useState({});
    const [invalidName, setInvalidName] = useState(false);
    const [formName, setFormName] = useState('');
    const [editable, setEditable] = useState(true);

    const { dialogName } = props;
    const dialogState = props.dialogs[dialogName];

    const { fromEditor, action } = dialogState.data;
    const form = fromEditor ? props.editorForm : props.managerForm;
    const urlFormId = form && form.urlFormId;

    const convertToEditorState = editorState => EditorState.createWithContent(convertFromRaw(editorState), decorator);

    useEffect(() => {
        setFormData(form);
        if (form) {
            if (form.confirmationMsg) setEditor(convertToEditorState(form.confirmationMsg));
            setEditData({ userId: form.createdBy.userId });
            setFormName(form.name);
            if (isEditAction(action)) {
                setEditable(isFormOwner(form, props.userId));
            }
            if (isCopyAction(action)) {
                form.name = getCopiedFormName(form.name);
                form.startDate = null;
                form.endDate = null;
                setFormData(form);
                setEditData({ name: form.name });
                setEditable(true);
            }
        }
    }, [form, action]);

    const handleSave = (action) => {
        if (isCopyAction(action)) {
            props.copyForm(editData, urlFormId)
            .then(closeDialog)
            .catch(({validationErrors}) => {
                const nameError = R.find(({fieldName}) => fieldName === "name", validationErrors);
                if (nameError) setInvalidName(nameError.message);
            });
        }
        else {
            const f = fromEditor ? props.updateFormSettingsFromEditor : props.updateFormSettingsFromManager;
            f(editData, urlFormId)
            .then(closeDialog)
            .catch(({validationErrors}) => {
                const nameError = R.find(({fieldName}) => fieldName === "name", validationErrors);
                if (nameError) setInvalidName(nameError.message);
            });
        }
    }

    const closeDialog = () => {
        handleClose(action);
    }

    const handleClose = (action) => {
        if (isCopyAction(action)) {
            props.getForms();
            props.resetSelectedForm();
        }
        else if (isCancelAction(action)) form.name = formName;    // restore form name in case of cancel
        props.closeDialog(dialogName);
        setInvalidName(false);
    };

    const update = (name, value) => {
        setFormData({ ...formData, [name]: value });
        setEditData({ ...editData, [name]: value });
    }

    const updateField = ({ target }) => {
        const value = target.type === 'checkbox' ? target.checked : target.value;

        update(target.name, value);
    }

    const updateDateField = ({ date, name }) => {
        update(name, date);
    }

    const updateDaysOfWeek = ({ days, name }) => update(name, days);

    const updateOrganizations = ({ target }) => {
        const currentIds = formData.organizationIds ? formData.organizationIds : [];
        const organizationIds = updateOrgs(target.value, target.checked, currentIds);
        return update('organizationIds', organizationIds)
    }

    return (
        <FormSettings
            form={formData}
            dialogState={dialogState}
            handleClose={handleClose}
            handleSave={handleSave}
            updateField={updateField}
            updateDateField={updateDateField}
            updateDaysOfWeek={updateDaysOfWeek}
            invalidName={invalidName}
            editorState={{ editor, setEditor }}
            organizations={props.organizations}
            updateOrganizations={updateOrganizations}
            action={action}
            editable={editable}
        />
    );
}

const mapStateToProps = (state) => ({
    dialogs: state.dialogs,
    editorForm: state.formEditor.form,
    managerForm: state.forms.selected,
    organizations: state.user.profile.organizations,
    userId: state.user.profile.userId
});

const mapDispatchToProps = (dispatch) => ({
    closeDialog: (dialog) => dispatch(closeDialog(dialog)),
    updateFormSettingsFromManager: (data, urlFormId) => dispatch(updateFormSettingsFromManager(data, urlFormId)),
    updateFormSettingsFromEditor: (data, urlFormId) => dispatch(updateFormSettingsFromEditor(data, urlFormId)),
    copyForm: (data, urlFormId) => dispatch(copyForm(data, urlFormId)),
    getForms: () => dispatch(getForms()),
    resetSelectedForm: () => dispatch(resetSelectedForm())
});

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(EditForm);
